import log from "loglevel";

import { Token } from "./definitions";
import { WIDGET_BRAND } from "./brand";
import { reportClientError } from "./utils/reportClientError";

const LOCAL_STORAGE_ITEM_ID = "TWILIO_WEBCHAT_WIDGET";

let _endpoint = "";

export async function contactBackend<T>(endpointRoute: string, body: Record<string, unknown> = {}): Promise<T> {
    const response = await fetch(_endpoint + endpointRoute, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        /*
         * 🔴 O STATUS VIAJA COM O ERRO, e a mensagem continua ESTÁTICA.
         *
         * Sem ele, um 403 (token recusado — problema nosso de credencial), um 502 (backend fora) e
         * uma falha de rede do apostador chegavam ao alerta como a MESMA linha, sem código nenhum.
         * São três investigações diferentes. `extrairCodigos` já lê `e.status`, `errorStatus` já
         * está na lista do POST e o backend já o imprime: faltava só o dado entrar no cano.
         *
         * O corpo da resposta NÃO entra — só o número. É o que mantém dado do apostador fora de um
         * alerta que vai para um app de mensagens.
         *
         * Efeito de segunda ordem, e é ganho: `sufixoDeCodigo` anexa o status ao motivo redigido, e a
         * chave de throttle do backend inclui o motivo. Um 403 e um 502 eram os dois
         * `"Error: falha técnica"` — mesma chave, e o segundo era engolido na janela de 5 min. Agora
         * são chaves diferentes e a operação vê os dois.
         */
        throw Object.assign(new Error("Request to backend failed"), { status: response.status });
    }

    return response.json();
}

/*
 * ⚠️ TODO acesso ao storage passa por aqui, e nenhum pode lançar.
 *
 * Não é paranoia: `localStorage` joga `SecurityError` no Safari em navegação privada, com storage
 * bloqueado por política do site, ou `QuotaExceededError` com a cota cheia. E isto roda no caminho
 * de BOOT do widget — onde uma exceção não degrada um pedaço da tela: derruba a árvore React inteira
 * e o chat SOME do site do apostador. Foi exatamente assim o incidente de 28/07.
 */
function lerDoStorage(): string | null {
    try {
        return localStorage.getItem(LOCAL_STORAGE_ITEM_ID);
    } catch (e) {
        reportClientError("session_storage_corrupted", { reason: "storage inacessível na leitura" });
        return null;
    }
}

/*
 * A chave do CSAT da sessão desta página — e, quando existe, é ela que manda.
 *
 * O `localStorage` lança no Safari em aba privada, com o armazenamento bloqueado pelo site e com a
 * cota cheia. A conversa segue funcionando na memória nesses casos, mas a chave, que só era lida do
 * storage, se perdia — e o apostador nunca via a pesquisa, sem erro e sem log.
 *
 * Por que a memória VENCE o storage, e não o contrário: com a cota cheia só a escrita falha, e a
 * leitura continua devolvendo a sessão ANTERIOR — a chave dela, mandada para a conversa nova, leva
 * 403. Então, depois que esta página criou, renovou ou retomou uma sessão, vale o que ela guardou
 * aqui, inclusive a ausência de chave. O storage só responde antes disso.
 *
 * Só a chave mora aqui: a retomada da sessão continua dependendo do storage, como antes. Spec em
 * `sessionDataHandler.test.ts`.
 */
let sessaoDaPagina: { csatKey?: string } | null = null;

function storeSessionData(data: Token) {
    sessaoDaPagina = { csatKey: data.csatKey };
    try {
        localStorage.setItem(LOCAL_STORAGE_ITEM_ID, JSON.stringify(data));
    } catch (e) {
        /*
         * Sem persistir, o apostador perde a conversa se recarregar — mas a sessão ATUAL continua
         * funcionando na memória. Degradar é muito melhor que quebrar o boot.
         */
        reportClientError("session_storage_corrupted", { reason: "storage inacessível na escrita" });
    }
}

function getStoredSessionData() {
    const item = lerDoStorage();
    let storedData: Token;

    if (!item) {
        return null;
    }

    try {
        storedData = JSON.parse(item);
    } catch (e) {
        log.log("Couldn't parse locally stored data");
        /*
         * Devolver `null` faz o apostador RECOMEÇAR do zero — ele perde a conversa que tinha. Era um
         * `log.log` no console dele e nada mais. Raro e auto-corrigível, por isso degradação; mas se
         * virar volume é sinal de bug NOSSO na escrita, e sem esta linha não haveria como perceber.
         *
         * Sem detalhe do conteúdo: o que estava gravado é dado da sessão do apostador.
         */
        reportClientError("session_storage_corrupted", { reason: "JSON da sessão local não parseou" });
        return null;
    }

    return storedData;
}

export const sessionDataHandler = {
    setEndpoint(endpoint: string = "") {
        _endpoint = endpoint;
    },

    getEndpoint() {
        return _endpoint;
    },

    /*
     * CSAT v2: a key HMAC guardada de carona na sessão (localStorage), com a cópia da memória para
     * quando o storage está bloqueado. Ausente = CSAT no-op.
     */
    getCsatKey(): string | undefined {
        return sessaoDaPagina ? sessaoDaPagina.csatKey : getStoredSessionData()?.csatKey;
    },

    tryResumeExistingSession(): Token | null {
        log.debug("sessionDataHandler: trying to refresh existing session");
        const storedTokenData = getStoredSessionData();

        if (!storedTokenData) {
            log.debug("sessionDataHandler: no tokens stored, no session to refresh");
            return null;
        }

        if (Date.now() >= new Date(storedTokenData.expiration).getTime()) {
            log.debug("sessionDataHandler: token expired, ignoring existing sessions");
            return null;
        }

        log.debug("sessionDataHandler: existing token still valid, using existing session data");
        // A sessão retomada passa a ser a desta página: se o storage parar de responder depois, a
        // chave da pesquisa continua aqui.
        sessaoDaPagina = { csatKey: storedTokenData.csatKey };
        return storedTokenData;
    },

    async getUpdatedToken(): Promise<Token> {
        log.debug("sessionDataHandler: trying to get updated token from BE");
        const storedTokenData = getStoredSessionData();

        if (!storedTokenData) {
            throw Error("Can't update token: current token doesn't exist");
        }

        let newTokenData: Token;

        try {
            newTokenData = await contactBackend<Token>("/refreshToken", {
                token: storedTokenData.token
            });
        } catch (e) {
            /*
             * Reembrulha PRESERVANDO o status. Sem ele, "token realmente recusado" e "backend
             * momentaneamente fora" ficavam indistinguíveis daqui — e é essa cegueira que faz a
             * reconexão gastar as três tentativas num token que já morreu (ver `reconnect.ts`).
             *
             * A interpolação `${e}` saiu porque não acrescentava nada de confiável: no caminho comum
             * ela repetia a nossa própria string fixa, e nos outros (rejeição do `fetch`, `json()` que
             * estoura) trazia texto de terceiro sem forma previsível. ⚠️ NÃO era um vazamento — `descreverErro`
             * substitui a mensagem de um `Error` por "falha técnica" antes de o motivo chegar ao
             * alerta (`descreverErro.ts:383-388`), então aquele texto nunca saía daqui.
             */
            const statusDeBaixo = (e as { status?: unknown } | null)?.status;
            throw Object.assign(
                new Error("Something went wrong when trying to get an updated token"),
                typeof statusDeBaixo === "number" ? { status: statusDeBaixo } : {}
            );
        }

        // Server won't return a conversation SID, so we merge the existing data with the latest one
        const updatedSessionData = {
            ...storedTokenData,
            ...newTokenData
        };

        storeSessionData(updatedSessionData);

        return updatedSessionData;
    },

    fetchAndStoreNewSession: async ({ formData }: { formData: Record<string, unknown> }) => {
        log.debug("sessionDataHandler: trying to create new session");

        let newTokenData;

        try {
            // A marca (fonte da verdade = este widget) acompanha a criação da conversa
            newTokenData = await contactBackend<Token>("/initWebchat", { formData, brand: WIDGET_BRAND });
        } catch (e) {
            /*
             * Mesmo motivo do `getUpdatedToken` logo acima, e este caminho é o que mais importa: é o
             * `/initWebchat` do pré-chat, que alimenta o `session_init_failed` — o alerta mais
             * frequente e mais mudo da produção. Descartar o erro aqui deixava este caminho cego
             * mesmo com o status já viajando desde o `contactBackend`.
             *
             * A mensagem continua a mesma string fixa: só o número acompanha.
             */
            const statusDeBaixo = (e as { status?: unknown } | null)?.status;
            throw Object.assign(
                new Error("No results from server"),
                typeof statusDeBaixo === "number" ? { status: statusDeBaixo } : {}
            );
        }

        log.debug("sessionDataHandler: new session successfully created");
        storeSessionData(newTokenData);

        return newTokenData;
    },

    clear: () => {
        // "Limpa nesta página", e não "ainda não sei": se o navegador não deixar apagar, o fallback
        // para o storage devolveria a chave da conversa que acabou de ser encerrada.
        sessaoDaPagina = { csatKey: undefined };
        try {
            localStorage.removeItem(LOCAL_STORAGE_ITEM_ID);
        } catch (e) {
            // Mesmo motivo dos outros dois: nenhum acesso ao storage pode derrubar o widget.
            reportClientError("session_storage_corrupted", { reason: "storage inacessível na limpeza" });
        }
    }
};
